<script setup lang="ts">
import InputText from 'primevue/inputtext'

interface Props {
  modelValue?: string | null
  type?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  disabled: false,
  invalid: false,
  size: 'medium',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur', event: FocusEvent): void
}>()

function handleUpdate(value: string | undefined) {
  emit('update:modelValue', value ?? '')
}
</script>

<template>
  <InputText
    :class="['base-input', `base-input--${size}`]"
    :modelValue="props.modelValue ?? ''"
    :type="type"
    :placeholder="placeholder"
    :disabled="disabled"
    :invalid="invalid"
    @update:modelValue="handleUpdate"
    @blur="(e) => emit('blur', e)"
  />
</template>

<style>
/* Unscoped — targets the InputText element PrimeVue renders, plus the
   pseudo-classes (:hover, :focus) that scoped CSS can't reach reliably
   when PrimeVue's pass-through attrs land on the inner element. */
.base-input.p-inputtext {
  border-radius: 6px;
  padding: 0 0.75rem;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.base-input.p-inputtext:focus,
.base-input.p-inputtext:focus-visible {
  border-color: var(--p-primary-500);
  box-shadow: 0 0 0 4px rgba(103, 85, 251, 0.12);
  outline: none;
}

.base-input.p-inputtext.p-invalid:focus,
.base-input.p-inputtext.p-invalid:focus-visible {
  border-color: var(--p-danger-500);
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.12);
}

.base-input--medium.p-inputtext {
  height: 42px;
}

.base-input--small.p-inputtext {
  height: 32px;
  font-size: 0.75rem;
}

/*
 * The money field the design uses for a listing price: taller than a form row
 * and set in the same 18px semibold as a card heading, so the amount reads as
 * the figure the screen is about rather than as one more filled-in field.
 * SF Pro Display has no upright 600, so this resolves to 700 — the same
 * fallback `Text`'s 18px `page-title` preset already relies on.
 */
.base-input--large.p-inputtext {
  height: 50px;
  font-size: 18px;
  font-weight: 600;
}
</style>
